import { useQuery } from '@tanstack/react-query';
import { isResultFromCurrentSession, withAuthSessionMark } from '../lib/authSessionGuard';
import { getMyPageInfoAPI } from '../api/mypage';
import { getItemWithExpiration, setItemWithExpiration } from '../lib/utils';
import { useRef } from 'react';

const POLLING_INTERVAL = 30 * 60 * 1000; // 30분

type UseSubscriptionPollingParams = {
    authObject: any;
    setAuthObject: (obj: any) => void;
    authorization: string | null;
    enabled?: boolean;
};

/**
 * 구독 상태 실시간 추적 훅
 * - 30분 간격 polling + 탭 포커스 시 즉시 refetch
 * - usableStatus 변경 감지 시 authObject + localStorage 갱신
 */
const useSubscriptionPolling = ({ authObject, setAuthObject, authorization, enabled = true }: UseSubscriptionPollingParams) => {
    const prevStatusRef = useRef<string | null>(authObject?.usableStatus ?? null);

    useQuery(
        ['subscriptionPolling', authObject?.centerId],
        // 조회를 시작한 세션 표식을 결과에 붙인다 — 늦은 응답이 다른 계정 저장소를 덮지 않게.
        async () => await withAuthSessionMark(() => getMyPageInfoAPI(authObject?.centerId, authorization as string), {
            authObject,
            authorization,
        }),
        {
            enabled: enabled && !!authObject?.centerId && !!authorization,
            refetchInterval: POLLING_INTERVAL,
            refetchOnWindowFocus: true,
            refetchIntervalInBackground: false,
            onSuccess: (res) => {
                if (res?.status !== 200) return;
                // 이 조회를 보낸 세션이 아직 지금 세션일 때만 쓴다 — 다른 계정 저장소에 섞지 않는다.
                if (!isResultFromCurrentSession(res)) return;

                const serverStatus = res.data;
                const currentStatus = prevStatusRef.current;
                const newStatus = serverStatus?.usableStatus;

                if (currentStatus === newStatus) return;

                prevStatusRef.current = newStatus;

                const currentAuth = getItemWithExpiration('authObject');
                if (!currentAuth) return;

                const updatedAuth = {
                    ...currentAuth,
                    usableStatus: serverStatus?.usableStatus,
                    paymentDueDate: serverStatus?.paymentDueDate,
                    planLimitCount: serverStatus?.planLimitCount,
                    prevPlanLimitCount: serverStatus?.prevPlanLimitCount,
                    changedPlanLimitCount: serverStatus?.changedPlanLimitCount,
                    confirmUsableStatus: false,
                };

                setItemWithExpiration('authObject', updatedAuth, 12);
                setAuthObject(updatedAuth);
            },
        }
    );
};

export default useSubscriptionPolling;
